// SPDX-License-Identifier: MIT
// Connectivity probe for SAS URL: blob containers and DFS directories

import { ContainerClient } from '@azure/storage-blob';

export async function testConnection(
  url: string
): Promise<{ ok: boolean; code?: string; error?: string }> {
  try {
    const u = new URL(url);
    const sr = u.searchParams.get('sr');
    const isDfsHost = /\.dfs\.core\.windows\.net$/i.test(u.hostname);
    const pathSegs = u.pathname.split('/').filter(Boolean);
    const hasDirInPath = pathSegs.length > 1; // <container>/<dir...>

    if (isDfsHost || sr === 'd' || hasDirInPath) {
      // Validate by listing directory paths via Data Lake API (non-recursive)
      try {
        const { DataLakeFileSystemClient } = (await import('@azure/storage-file-datalake')) as any;
        const container = pathSegs[0];
        const dirPath = pathSegs.slice(1).join('/');
        const fsUrl = `https://${u.hostname}/${container}?${u.searchParams.toString()}`;
        const fsClient = new DataLakeFileSystemClient(fsUrl);
        const iter = fsClient
          .listPaths({ path: dirPath || undefined, recursive: false })
          .byPage({ maxPageSize: 1 } as any);
        await iter.next();
        return { ok: true };
      } catch (e) {
        const err = e as Error & { message?: string };
        const msg = err?.message || 'Unknown error';
        if (/Signature fields not well formed/i.test(msg))
          return { ok: false, code: 'AUTH_INVALID', error: msg };
        if (/AuthenticationFailed/i.test(msg))
          return { ok: false, code: 'AUTH_INVALID', error: msg };
        if (/AuthorizationPermissionMismatch/i.test(msg))
          return { ok: false, code: 'AUTH_PERMISSIONS', error: msg };
        if (/403/.test(msg)) return { ok: false, code: 'AUTH_FORBIDDEN', error: msg };
        return { ok: false, code: 'UNKNOWN', error: msg };
      }
    }

    // Default: blob container scope validation
    const containerClient = new ContainerClient(url);
    const iter = containerClient.listBlobsFlat().byPage({ maxPageSize: 1 });
    await iter.next();
    return { ok: true };
  } catch (e) {
    const err = e as Error & { message?: string };
    const msg = err?.message || 'Unknown error';
    if (/Signature fields not well formed/i.test(msg))
      return { ok: false, code: 'AUTH_INVALID', error: msg };
    if (/AuthenticationFailed/i.test(msg)) return { ok: false, code: 'AUTH_INVALID', error: msg };
    if (/AuthorizationPermissionMismatch/i.test(msg))
      return { ok: false, code: 'AUTH_PERMISSIONS', error: msg };
    if (/403/.test(msg)) return { ok: false, code: 'AUTH_FORBIDDEN', error: msg };
    return { ok: false, code: 'UNKNOWN', error: msg };
  }
}
